import './styles/parts.scss';

import {
    Breadcrumb,
    Button,
    Icon,
    Loading,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { Document, Page, pdfjs } from 'react-pdf';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    faChevronLeft,
    faChevronRight,
    faMinus,
    faPlus,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import AvailabilityComponent from '../components/Primitives/AvailabilityComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FsLightbox from 'fslightbox-react';
import { GatsbyImage } from 'gatsby-plugin-image';
import GatsbyImageWrapper from '../components/GatsbyImageWrapper';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import WZoom from 'vanilla-js-wheel-zoom/src/wheel-zoom';
import arrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Arrow-Icon.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { graphql } from 'gatsby';
import popFrontURL from '../utils/popFrontURL';
import { pushAddToCartEvent } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useGTM_UA';
import rightArrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Arrow-Icon-White.svg';
import { useAddProductsToCartAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart/actions/useAddProductsToCartAction';
import { useAllProducts } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useAllProducts';
import { useCartContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($code: String!) {
        productsJson(code: { eq: $code }) {
            code
            id
            title {
                en_CA
                en_US
                fr_CA
            }
            slug {
                en_CA
                en_US
                fr_CA
            }
            images {
                en_CA
                en_US
                fr_CA
            }
            fields {
                ...productsFieldsImagesFragment
                schema {
                    childImageSharp {
                        gatsbyImageData(layout: CONSTRAINED)
                    }
                }
            }
            schema {
                en_CA
                fr_CA
                en_US
            }
            parts {
                code
                id
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    schema_number {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    nb_per_appliance {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
            attributes {
                serial_number_from {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                serial_number_to {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
            }
        }
        metadata: allProductsJson(
            filter: { code: { eq: "englander_pieces" } }
        ) {
            nodes {
                code
                images {
                    fr_CA
                    en_CA
                    en_US
                }
                fields {
                    ...productsFieldsImagesFragment
                }
            }
        }
    }
`;

export default function Parts({ data }) {
    const product = data.productsJson;
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const [wzoom, setWzoom]: [any, any] = useState({});
    const imgRef = useRef();

    const partImagePlaceholder =
        data?.metadata?.nodes[0]?.fields?.images[0]?.childImageSharp
            ?.gatsbyImageData;

    const isMobile = useMobileDetect().isMobile();
    const [pdfScale, setPdfScale] = useState(isMobile ? 80 : 100);
    const [coords, setCoords] = useState([]);
    const [pdfPage, setPdfPage] = useState(null);
    const [pageNumber] = useState(1);
    const [pdfAnnotations, setAnnotations] = useState([]);

    const getSchemaAnnotations = async (schema: string) => {
        const pdfDocument = await pdfjs
            .getDocument(`/file/${schema}`)
            .promise.then((doc) => {
                return doc;
            });
        const page = await pdfDocument.getPage(1);
        const annotations = await page.getAnnotations();
        setPdfPage(page);
        setAnnotations(annotations);
    };

    useEffect(() => {
        setWzoom(1); // Force un refresh du component
        for (let index = 0; index < pdfAnnotations.length; index++) {
            const rect = pdfAnnotations[index].rect;
            const view = pdfPage?.view;
            const normalizedRect = pdfjs.Util.normalizeRect([
                rect[0],
                view[3] - rect[1],
                rect[2],
                view[3] - rect[3],
            ]);
            const newCoord = {
                x: normalizedRect[0],
                y: normalizedRect[1],
                note: pdfAnnotations?.[index]?.unsafeUrl,
            };
            const newCoords = coords;
            newCoords.push(newCoord);
            setCoords(newCoords);
        }
    }, [coords, pdfAnnotations, pdfPage?.view]);

    useEffect(() => {
        setWzoom(
            WZoom.create('#parts-img', {
                zoomOnClick: false,
                maxScale: 10,
                minScale: 0.35,
                speed: 12,
            })
        );
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    }, []);

    const explodedParts = [];
    product?.parts?.forEach((part: any) => {
        const schemaNumber =
            part?.attributes?.schema_number?.values[0][currentLocale];

        if (schemaNumber?.includes(',')) {
            schemaNumber.split(',').forEach((partNumber) => {
                const newPart = JSON.parse(JSON.stringify(part));

                newPart.attributes.schema_number.values[0][currentLocale] =
                    partNumber;

                explodedParts.push(newPart);
            });
        } else {
            explodedParts.push(part);
        }
    });

    const [filteredParts, setFilteredParts] = useState(explodedParts);

    const comparePart = (a, b, defaultValue) => {
        let intervalStart;

        if (a?.includes(',')) {
            intervalStart = a?.substring(0, 2);
            return intervalStart - b;
        } else if (isNaN(a)) {
            a = defaultValue;
        }

        if (b?.includes(',')) {
            intervalStart = b?.substring(0, 2);
            return a - intervalStart;
        } else if (isNaN(b)) {
            b = defaultValue;
        }

        return a - b;
    };

    const serialNumberFrom =
        product.attributes?.serial_number_from?.values[0]['en_CA'];
    const serialNumberTo =
        product.attributes?.serial_number_to?.values[0]['en_CA'];

    const [schemaIndex, setSchemaIndex] = useState(0);

    function updateSchemaIndex(modifier: number) {
        if (
            schemaIndex + modifier >= 0 &&
            schemaIndex + modifier < product?.schema?.length
        )
            setSchemaIndex(schemaIndex + modifier);
    }
    function updatePdfScale(modifier: number) {
        if (pdfScale + modifier > 0 && pdfScale + modifier < 200)
            setPdfScale(pdfScale + modifier);
    }

    const endsWithPdf =
        product?.schema?.[schemaIndex][currentLocale]?.endsWith('.pdf');
    const hasMultipleSchema = product?.schema?.length > 1;

    const [isPartsFilterOpen, setPartsFilterOpen] = useState(false);
    const [partsFilterState, setPartsFilterState] = useState('asc');

    useEffect(() => {
        if (partsFilterState === 'asc') {
            setFilteredParts(
                explodedParts.sort((a: any, b: any) =>
                    comparePart(
                        a?.attributes?.schema_number?.values[0][currentLocale],
                        b?.attributes?.schema_number?.values[0][currentLocale],
                        Number.MAX_VALUE
                    )
                )
            );
        } else if (partsFilterState === 'desc') {
            //
            setFilteredParts(
                explodedParts.sort((a: any, b: any) =>
                    comparePart(
                        b?.attributes?.schema_number?.values[0][currentLocale],
                        a?.attributes?.schema_number?.values[0][currentLocale],
                        Number.MIN_VALUE
                    )
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partsFilterState]);

    return (
        <Layout>
            <SEO
                title={t('metadata:part.title', {
                    SKU: product?.code,
                })}
                description={product?.title?.[currentLocale]}
            />

            <div className='container parts'>
                <Breadcrumb
                    elements={[
                        { link: '/', name: t('layout:appliance.home') },
                        { link: '/archives/', name: t('layout:header.parts') },
                        { link: '', name: product?.code },
                    ]}
                />
                <div className='parts-banner-wrapper'>
                    <GatsbyImageWrapper
                        className='product-image'
                        fields={product.fields}
                    />
                    <div className='product-info'>
                        <span className='code'>{product?.code}</span>
                        <h1 className='title'>
                            {product?.title?.[currentLocale]}
                        </h1>
                        <span className='serial'>
                            {serialNumberFrom && serialNumberTo
                                ? t('parts.serial-fromto', {
                                      from: serialNumberFrom,
                                      to: serialNumberTo,
                                  })
                                : serialNumberFrom === undefined
                                  ? '--'
                                  : serialNumberFrom + t('parts.serial-from')}
                        </span>
                    </div>

                    {!product.slug['en_CA'].includes('discontinued') && (
                        // On affiche le boutton seulement si le produit n'est pas discontinué
                        <div className='button-container'>
                            <Button
                                className='englander-button'
                                label={t('parts.view-product')}
                                link={popFrontURL(
                                    product.slug[currentLocale],
                                    2
                                )}
                                iconRight={rightArrowIcon}
                            />
                        </div>
                    )}
                </div>

                <div className='primary'>
                    {product?.schema && (
                        <div className='schema-slider'>
                            {endsWithPdf && hasMultipleSchema && (
                                <span className='page-nb'>{`${
                                    schemaIndex + 1
                                } / ${product?.schema?.length}`}</span>
                            )}
                            {endsWithPdf && hasMultipleSchema && (
                                <FontAwesomeIcon
                                    className='arrow left'
                                    icon={faChevronLeft}
                                    onClick={() => updateSchemaIndex(-1)}
                                />
                            )}
                            {endsWithPdf && (
                                <div className='pdf-scale'>
                                    <FontAwesomeIcon
                                        className='scale'
                                        icon={faPlus}
                                        onClick={() => updatePdfScale(20)}
                                    />
                                    <FontAwesomeIcon
                                        className='scale'
                                        icon={faMinus}
                                        onClick={() => updatePdfScale(-20)}
                                    />
                                </div>
                            )}

                            <div
                                className={
                                    endsWithPdf
                                        ? 'part-schema pdf'
                                        : 'part-schema'
                                }
                            >
                                {endsWithPdf ? (
                                    <Document
                                        className='pdf-doc'
                                        file={`/file/${product?.schema?.[schemaIndex]?.[currentLocale]}`}
                                        loading={<Loading />}
                                    >
                                        <Page
                                            scale={pdfScale * 0.01}
                                            pageNumber={pageNumber}
                                            onLoadSuccess={() =>
                                                getSchemaAnnotations(
                                                    product?.schema?.[
                                                        schemaIndex
                                                    ]?.[currentLocale]
                                                )
                                            }
                                        />

                                        {coords.map((coord, index) => {
                                            return (
                                                <PartPreview
                                                    key={index}
                                                    coord={coord}
                                                    allParts={filteredParts}
                                                    currentLocale={
                                                        currentLocale
                                                    }
                                                />
                                            );
                                        })}
                                    </Document>
                                ) : (
                                    <>
                                        <div className='schema-zoom'>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomDown();
                                                }}
                                            >
                                                &#8722;
                                            </button>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomUp();
                                                }}
                                            >
                                                &#43;
                                            </button>
                                        </div>
                                        <div className='img-container'>
                                            {product?.schema &&
                                                product?.fields?.schema && (
                                                    <img
                                                        alt=''
                                                        className='parts-img'
                                                        id='parts-img'
                                                        ref={imgRef}
                                                        src={
                                                            product?.fields
                                                                ?.schema?.[
                                                                schemaIndex
                                                            ]?.childImageSharp
                                                                ?.gatsbyImageData
                                                                ?.images
                                                                ?.fallback?.src
                                                        }
                                                        draggable={false}
                                                    />
                                                )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {endsWithPdf && hasMultipleSchema && (
                                <FontAwesomeIcon
                                    className='arrow right'
                                    icon={faChevronRight}
                                    onClick={() => updateSchemaIndex(1)}
                                />
                            )}
                        </div>
                    )}

                    <div className='part-list'>
                        <div className='part-list-header'>
                            <span>{t('parts.sold-individually')}</span>
                            <div className='parts-filter-container'>
                                <div
                                    className='parts-filter'
                                    onClick={() =>
                                        setPartsFilterOpen(!isPartsFilterOpen)
                                    }
                                >
                                    <span>{t('parts.sort')}</span>
                                    <img
                                        className={`iconRight`}
                                        src={arrowIcon}
                                        alt=''
                                        style={
                                            isPartsFilterOpen
                                                ? {
                                                      transform:
                                                          'rotate(-90deg)',
                                                  }
                                                : { transform: 'rotate(90deg)' }
                                        }
                                    />
                                </div>

                                {isPartsFilterOpen && (
                                    <div className='parts-filter-options'>
                                        <span
                                            className='parts-filter-option'
                                            onClick={() => {
                                                setPartsFilterState('asc');
                                                setPartsFilterOpen(false);
                                            }}
                                            style={{
                                                textDecoration:
                                                    partsFilterState === 'asc'
                                                        ? 'underline'
                                                        : 'none',
                                            }}
                                        >
                                            {t('parts.sort-ascending')}
                                        </span>
                                        <span
                                            className='parts-filter-option'
                                            onClick={() => {
                                                setPartsFilterState('desc');
                                                setPartsFilterOpen(false);
                                            }}
                                            style={{
                                                textDecoration:
                                                    partsFilterState === 'desc'
                                                        ? 'underline'
                                                        : 'none',
                                            }}
                                        >
                                            {t('parts.sort-descending')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className='list'
                            style={
                                product?.schema
                                    ? { height: '80vh', overflowY: 'scroll' }
                                    : {}
                            }
                        >
                            {filteredParts.map((part, index) => {
                                return (
                                    <Part
                                        key={index}
                                        part={part}
                                        partImagePlaceholder={
                                            partImagePlaceholder
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function Part({ part, partImagePlaceholder }) {
    const [qtyValue, setQtyValue] = useState(1);
    const [price] = useProductPrice(part?.code);
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const [allProducts] = useAllProducts();
    const partImage = allProducts.nodes.find(
        (product) => product.code === part?.code
    )?.fields?.images?.[0]?.childImageSharp?.gatsbyImageData;
    const [isLightBoxVisible, setIsLightBoxVisible] = useState(false);

    return (
        <div className='part-single'>
            <div className='part-number'>
                <span>
                    {part?.attributes?.schema_number?.values[0][
                        currentLocale
                    ] ?? '-'}
                </span>
            </div>
            <div className='part-info-container'>
                <div
                    className='part-image'
                    onClick={() => setIsLightBoxVisible(!isLightBoxVisible)}
                >
                    <FsLightbox
                        toggler={isLightBoxVisible}
                        exitFullscreenOnClose={true}
                        sources={
                            partImage
                                ? [partImage?.images?.fallback?.src]
                                : [partImagePlaceholder?.images?.fallback?.src]
                        }
                    />
                    <GatsbyImage
                        image={partImage ? partImage : partImagePlaceholder}
                        alt=''
                    />
                </div>

                <div className='part-info'>
                    <span className='code'>{part?.code}</span>
                    <span className='title'>
                        {part?.title && part?.title[currentLocale]}
                    </span>
                    <span className='nb-per-appliance'>
                        {
                            part?.attributes?.nb_per_appliance?.values[0][
                                currentLocale
                            ]
                        }
                        {t('parts.nb-per-appliance')}
                    </span>
                </div>
            </div>
            <div className='part-price-info'>
                <div className='part-info'>
                    <div className='part-price'>
                        {price?.regularPrice?.value === 0
                            ? '--'
                            : LocalePrice(
                                  currentLocale,
                                  price?.regularPrice?.value
                              )}
                    </div>
                    <AvailabilityComponent product={part} />
                </div>
                <AddToCartButtonContainer
                    product={part}
                    qtyValue={qtyValue}
                    setQtyValue={setQtyValue}
                    showCartIcon={false}
                />
            </div>
        </div>
    );
}

function PartPreview(props: { coord; allParts; currentLocale }) {
    const [isVisible, setIsVisible] = useState(false);
    const { coord, allParts, currentLocale } = props;
    const svgCart =
        'M9.384,19.186a1.7,1.7,0,1,1-1.7-1.7,1.7,1.7,0,0,1,1.7,1.7Zm11.355,0a1.7,1.7,0,1,1-1.7-1.7A1.7,1.7,0,0,1,20.739,19.186Zm0-7.381V4.992H7.113A1.136,1.136,0,0,0,5.977,3.856H2.571V4.992H4.842L5.695,12.3a2.271,2.271,0,0,0,1.418,4.044H20.739V15.211H7.113a1.136,1.136,0,0,1-1.136-1.136s0-.008,0-.012L20.739,11.8Z';
    const code = coord?.note?.split('//')?.[1].replace(':', '');

    const part = allParts.find((part) => part?.code === code);
    const [price] = useProductPrice(code);
    const [addProductToCart] = useAddProductsToCartAction();
    const { addAlert } = useContext(feedbackContext);
    const { setQuickCartVisible } = useCartContext();

    return (
        <React.Fragment>
            <div
                className='coord-test'
                style={{
                    left: coord.x,
                    top: coord.y,
                }}
                onClick={() => setIsVisible(!isVisible)}
            >
                {isVisible && (
                    <div
                        className='preview'
                        onClick={(event) => event.stopPropagation()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            className='close'
                            onClick={() => setIsVisible(false)}
                        />
                        <div className='part-number'>
                            <span>
                                {
                                    part?.attributes?.schema_number?.values[0][
                                        currentLocale
                                    ]
                                }
                            </span>
                        </div>
                        <div className='title'>
                            <span>{part?.title[currentLocale]}</span>
                        </div>
                        <span className='code'>{part?.code}</span>
                        <div className='price'>
                            <span>
                                {price?.regularPrice?.value === 0
                                    ? '--'
                                    : LocalePrice(
                                          currentLocale,
                                          price?.regularPrice?.value
                                      )}
                            </span>
                        </div>
                        <div className='addToCart'>
                            <div
                                className='buy-icon'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setQuickCartVisible(true);
                                    addProductToCart(code, 1)
                                        .then(() => {
                                            // console.log(res);
                                            // GTM GA4
                                            pushAddToCartEvent({
                                                id: code,
                                                price: price?.finalPrice.value,
                                                currency:
                                                    price?.finalPrice.currency,
                                                qty: 1,
                                            });
                                        })
                                        .catch((error) => {
                                            console.log(error);
                                            addAlert({
                                                message: error,
                                                status: 'error',
                                            });
                                        });
                                }}
                            >
                                <span>
                                    <span>+</span>
                                    <Icon
                                        d={svgCart}
                                        size='20'
                                        viewbox='0 0 18.169 17.033'
                                        transform='translate(-2.571 -3.856)'
                                    />
                                </span>
                            </div>
                        </div>

                        <div className='arrow-down' />
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
